import { useState } from 'react'
import { kbCanRetry, kbIsSettling, type KbSource } from '@rp/contracts'
import { Badge, Button, Dialog, EmptyState, LedgerRow } from '../../design'
import { formatDateTime } from '../../lib/time'
import { KB_COPY, KB_SOURCE_STATUS_LABELS, kbFailureText, kbStatusTone } from './copy'

export type KbRetryError = { id: string; status: KbSource['status']; message: string }
type DownloadProps = {
  onDownload: (source: Pick<KbSource, 'id' | 'title'>) => void
  downloadingIds: ReadonlySet<string>
  downloadErrors: ReadonlyMap<string, string>
}

type RetryProps = {
  onRetry: (source: Pick<KbSource, 'id' | 'status'>) => void
  retryingId: string | null
  retryError: KbRetryError | null
}

const showsRefusal = (source: KbSource, error: KbRetryError | null): boolean =>
  error !== null && error.id === source.id && error.status === source.status

const ActionError = ({ message }: { message: string | null }) =>
  message === null ? null : (
    <p className="tone-danger kb-source-error" role="alert">
      {message}
    </p>
  )

const DownloadButton = ({ source, download }: { source: KbSource; download: DownloadProps }) =>
  source.kind === 'upload' ? (
    <Button
      pending={download.downloadingIds.has(source.id)}
      pendingLabel={KB_COPY.downloading}
      onClick={() => download.onDownload(source)}
    >
      {KB_COPY.download}
    </Button>
  ) : null

const KbSourceActions = ({
  source,
  removing,
  onRemove,
  retry,
  download,
}: {
  source: KbSource
  removing: boolean
  onRemove: () => void
  retry: RetryProps
  download: DownloadProps
}) => (
  <>
    <ActionError message={showsRefusal(source, retry.retryError) ? (retry.retryError?.message ?? null) : null} />
    <ActionError message={download.downloadErrors.get(source.id) ?? null} />
    <div className="kb-source-actions">
      <DownloadButton source={source} download={download} />
      {kbCanRetry(source) ? (
        <Button
          pending={retry.retryingId === source.id}
          pendingLabel={KB_COPY.retryingIngest}
          onClick={() => retry.onRetry(source)}
        >
          {KB_COPY.retryIngest}
        </Button>
      ) : null}
      <Button variant="danger" pending={removing} pendingLabel={KB_COPY.removing} onClick={onRemove}>
        {KB_COPY.remove}
      </Button>
    </div>
  </>
)

const KbSourceRow = ({
  source,
  pending,
  onRemove,
  retry,
  download,
}: {
  source: KbSource
  pending: boolean
  onRemove: () => void
  retry: RetryProps
  download: DownloadProps
}) => {
  const settling = kbIsSettling(source.status)

  return (
    <LedgerRow>
      <div className="kb-source">
        <div className="kb-source-head">
          <span className="kb-source-title">{source.title}</span>
          <Badge tone={kbStatusTone(source.status)}>{KB_SOURCE_STATUS_LABELS[source.status]}</Badge>
        </div>
        <p className="kb-source-meta">
          <span className="num">{KB_COPY.chunkCount(source.chunkCount)}</span>
          <span className="num">{KB_COPY.version(source.version)}</span>
          <span className="num">{formatDateTime(new Date(source.updatedAt))}</span>
          {source.uri === null ? null : <span className="num kb-source-uri">{source.uri}</span>}
        </p>
        {settling ? <div className="kb-source-ingest hatch" aria-hidden="true" /> : null}
        {source.error === null || settling ? null : (
          <p className="tone-danger kb-source-error" role="status">
            {kbFailureText(source)}
          </p>
        )}
        <KbSourceActions source={source} removing={pending} onRemove={onRemove} retry={retry} download={download} />
      </div>
    </LedgerRow>
  )
}

export const KbSourceList = ({
  sources,
  onDelete,
  deletingId,
  download,
  ...retry
}: {
  sources: readonly KbSource[]
  onDelete: (id: string) => void
  deletingId: string | null
  download: DownloadProps
} & RetryProps) => {
  const [confirming, setConfirming] = useState<KbSource | null>(null)

  if (sources.length === 0) return <EmptyState title={KB_COPY.empty} description={KB_COPY.emptyBody} />

  return (
    <>
      <ul className="kb-list" aria-label={KB_COPY.sources}>
        {sources.map((source) => (
          <KbSourceRow
            key={source.id}
            source={source}
            pending={deletingId === source.id}
            onRemove={() => setConfirming(source)}
            retry={retry}
            download={download}
          />
        ))}
      </ul>
      <Dialog
        open={confirming !== null}
        title={KB_COPY.confirmRemoveTitle}
        onClose={() => setConfirming(null)}
        footer={
          <>
            <Button onClick={() => setConfirming(null)}>{KB_COPY.confirmRemoveCancel}</Button>
            <Button
              variant="danger"
              onClick={() => {
                if (confirming === null) return
                onDelete(confirming.id)
                setConfirming(null)
              }}
            >
              {KB_COPY.confirmRemoveConfirm}
            </Button>
          </>
        }
      >
        <p>{confirming === null ? '' : KB_COPY.confirmRemoveBody(confirming.title)}</p>
      </Dialog>
    </>
  )
}
